import type { PayloadAction } from '@reduxjs/toolkit';
import type { Draft } from 'immer';
import isDeepEqual from 'lodash/isEqual';

import { isCategoryLabel } from '@proton/mail/helpers/location';
import { safeDecreaseCount, safeIncreaseCount } from '@proton/redux-utilities/helpers/safeCount';
import { MAILBOX_LABEL_IDS } from '@proton/shared/lib/constants';
import { toMap } from '@proton/shared/lib/helpers/object';
import { SentryMailInitiatives, traceInitiativeError } from '@proton/shared/lib/helpers/sentry';
import type { Folder, Label } from '@proton/shared/lib/interfaces';
import type { Message, MessageMetadata } from '@proton/shared/lib/interfaces/mail/Message';
import { MARK_AS_STATUS } from '@proton/shared/lib/mail/constants';
import diff from '@proton/utils/diff';
import isTruthy from '@proton/utils/isTruthy';
import range from '@proton/utils/range';
import unique from '@proton/utils/unique';

import {
    getElementContextIdentifier,
    hasLabel,
    isElementConversation,
    isElementMessage,
    parseLabelIDsInEvent,
} from '../../helpers/elements';
import type { Conversation } from '../../models/conversation';
import type { Element } from '../../models/element';
import type { ConversationParams, ConversationResult } from '../conversations/conversationsTypes';
import {
    applyLabelToConversation,
    applyLabelToConversationMessage,
    applyLabelToMessage,
    removeLabelFromConversation,
    removeLabelFromMessage,
    removeLabelToConversationMessage,
} from '../mailbox/locationHelpers';
import type { filterSubscriptionList } from '../newsletterSubscriptions/newsletterSubscriptionsActions';
import { newElementsState } from './elementsInitialState';
import type {
    ESResults,
    ElementsState,
    ElementsStateParams,
    EventUpdates,
    NewStateParams,
    OptimisticDelete,
    OptimisticUpdates,
    QueryParams,
    QueryResults,
    TaskRunningInfo,
} from './elementsTypes';
import { getElementsToBypassFilter } from './helpers/elementBypassFilters';
import {
    computeContextTotals,
    updateContextTotals,
    updateDeletedSinceLastLoadFromContextTotals,
} from './helpers/elementContextCount';
import { newRetry } from './helpers/elementQuery';

const getContextIdentifierFromParams = (params: ElementsStateParams) =>
    getElementContextIdentifier({
        labelID: params.labelID,
        categoryIDs: params.categoryIDs,
        conversationMode: params.conversationMode,
        filter: params.filter,
        sort: params.sort,
        from: params.search.from,
        to: params.search.to,
        address: params.search.address,
        begin: params.search.begin,
        end: params.search.end,
        keyword: params.search.keyword,
        newsletterSubscriptionID: params.newsletterSubscriptionID,
    });

export const globalReset = (state: Draft<ElementsState>) => {
    Object.assign(state, newElementsState());
};

export const reset = (state: Draft<ElementsState>, action: PayloadAction<NewStateParams>) => {
    Object.assign(
        state,
        newElementsState({
            ...action.payload,
            taskRunning: { labelIDs: state.taskRunning.labelIDs, timeoutID: state.taskRunning.timeoutID },
        })
    );
};

export const updatePage = (state: Draft<ElementsState>, action: PayloadAction<number>) => {
    state.page = action.payload;
};

export const retry = (
    state: Draft<ElementsState>,
    action: PayloadAction<{ queryParameters: unknown; error: Error | undefined }>
) => {
    state.beforeFirstLoad = false;
    state.invalidated = false;
    state.pendingRequest = false;
    state.retry = newRetry(state.retry, state.params, action.payload.error);
};

export const loadPending = (
    state: Draft<ElementsState>,
    action: PayloadAction<undefined, string, { arg: QueryParams }>
) => {
    const { refetch, page } = action.meta.arg;

    if (!refetch) {
        state.pendingRequest = true;
        state.page = page;
    }
};

export const loadFulfilled = (
    state: Draft<ElementsState>,
    action: PayloadAction<
        { result: QueryResults; taskRunning: TaskRunningInfo; params: ElementsStateParams },
        string,
        { arg: QueryParams }
    >
) => {
    const { page, refetch } = action.meta.arg;
    const {
        result: { Total, Stale },
        taskRunning,
        // Always use params from the request, and do not use params from the state
        // Otherwise, concurrent requests will update the wrong "context filter"
        params,
    } = action.payload;

    const isResponseStale = Stale === 1;

    const contextFilter = getContextIdentifierFromParams(params);

    Object.assign(state, {
        beforeFirstLoad: false,
        invalidated: false,
        pendingRequest: false,
        page: refetch ? state.page : page,
        retry: newRetry(state.retry, state.params, undefined),
        taskRunning,
    });

    // The server search reads metadata only, and it is also where Encrypted Search falls back to.
    // Scoped to the response's own context so a load resolving for a context the user has already
    // left cannot clear the flag a later Encrypted Search set.
    if (contextFilter === getContextIdentifierFromParams(state.params)) {
        state.usedEncryptedSearch = false;
    }

    state.awaitingStaleRetry[contextFilter] = isResponseStale;

    if (!state.taskRunning.labelIDs.includes(params.labelID) && !isResponseStale) {
        state.total[contextFilter] = Total;
    }

    // Once we get a fresh (non-stale) response, the pages we fetched account for everything
    // deleted so far, so the counter driving the dynamic page fetch count can be cleared.
    if (!isResponseStale) {
        state.deletedSinceLastLoad = 0;
    }
};

/**
 * This reducer is used to set first loaded elements while loading remaining ones in series
 */
export const showSerializedElements = (
    state: Draft<ElementsState>,
    action: PayloadAction<{ result: QueryResults; page: number; params: ElementsStateParams }, string>
) => {
    // Always use params from the request, and do not use params from the state
    // Otherwise, concurrent requests will update the wrong "context filter"
    const params = action.payload.params;
    const {
        result: { Total, Stale, Elements },
        page,
    } = action.payload;

    if (state.taskRunning.labelIDs.includes(params.labelID)) {
        return;
    }

    const contextFilter = getElementContextIdentifier({
        labelID: params.labelID,
        categoryIDs: params.categoryIDs,
        conversationMode: params.conversationMode,
        filter: params.filter,
        sort: params.sort,
        from: params.search.from,
        to: params.search.to,
        address: params.search.address,
        begin: params.search.begin,
        end: params.search.end,
        keyword: params.search.keyword,
        newsletterSubscriptionID: params.newsletterSubscriptionID,
    });

    // We only insert elements that are not already in the state. The event loop updates existing items.
    Elements.forEach((element) => {
        if (element.ID && !state.elements[element.ID]) {
            state.elements[element.ID] = element;
        }
    });

    if (Stale === 0 || !state.total[contextFilter]) {
        state.total[contextFilter] = Total;
    }

    state.pages[contextFilter] = state.pages[contextFilter]
        ? unique([...state.pages[contextFilter], page]).sort()
        : [page];
};

export const manualPending = (state: Draft<ElementsState>) => {
    state.pendingRequest = true;
};

export const manualFulfilled = (state: Draft<ElementsState>) => {
    state.pendingRequest = false;
};

export const esSearchStarted = (state: Draft<ElementsState>) => {
    state.pendingESSearches += 1;
};

export const esSearchSettled = (state: Draft<ElementsState>) => {
    state.pendingESSearches = Math.max(0, state.pendingESSearches - 1);
};

export const removeExpired = (state: Draft<ElementsState>, action: PayloadAction<Element>) => {
    delete state.elements[action.payload.ID || ''];
};

export const invalidate = (state: Draft<ElementsState>) => {
    state.invalidated = true;
};

export const eventUpdatesPending = (
    state: Draft<ElementsState>,
    action: PayloadAction<undefined, string, { arg: EventUpdates }>
) => {
    const countsBeforeAction = computeContextTotals(state);

    const { toCreate, toUpdate, toDelete } = action.meta.arg;
    toCreate.forEach((element) => {
        state.elements[element.ID] = element;
    });

    // We only update elements already in state, not blindly insert new ones.
    // Inserting unknown elements would create phantom entries in views with page gaps,
    // showing results before the relevant page has been loaded.
    // The trade-off is potential context total drift: if an element moves out of the
    // current context but isn't in state, updateContextTotals won't detect the change.
    toUpdate.forEach((element) => {
        const existingElement = state.elements[element.ID];
        if (existingElement) {
            state.elements[element.ID] = parseLabelIDsInEvent(existingElement, element);
        }
    });

    toDelete.forEach((elementID) => {
        delete state.elements[elementID];
    });

    const countsAfterAction = computeContextTotals(state);
    updateContextTotals({ state, countsBeforeAction, countsAfterAction });
};

export const eventUpdatesFulfilled = (
    state: Draft<ElementsState>,
    action: PayloadAction<(Element | undefined)[], string, { arg: EventUpdates }>
) => {
    const countsBeforeAction = computeContextTotals(state);

    action.payload.filter(isTruthy).forEach((element) => {
        state.elements[element.ID] = element;
    });

    const countsAfterAction = computeContextTotals(state);
    updateContextTotals({ state, countsBeforeAction, countsAfterAction });
};

export const addESResults = (state: Draft<ElementsState>, action: PayloadAction<ESResults>) => {
    const total = action.payload.elements.length;
    const pagesArray = range(0, Math.ceil(total / action.payload.pageSize));
    // If the resulting array is empty because no results have been found,
    // cache the page 0, so that we do not trigger additional searches.
    const pages = pagesArray.length === 0 ? [0] : pagesArray;

    const params = action.payload.params;
    const contextFilter = getContextIdentifierFromParams(params);

    state.bypassFilter = [];
    state.beforeFirstLoad = false;
    state.invalidated = false;
    state.pendingRequest = false;
    state.page = action.payload.page;
    state.usedEncryptedSearch = true;
    state.elements = { ...state.elements, ...toMap(action.payload.elements, 'ID') };
    state.retry = { payload: undefined, count: 0, error: undefined };
    state.total[contextFilter] = total;
    state.pages[contextFilter] = state.pages[contextFilter]
        ? unique([...state.pages[contextFilter], ...pages]).sort()
        : [...pages];
};

export const optimisticUpdates = (state: Draft<ElementsState>, action: PayloadAction<OptimisticUpdates>) => {
    action.payload.elements.forEach((element) => {
        if (element.ID) {
            state.elements[element.ID] = element;
        }
    });

    const params = state.params;

    const contextFilter = getElementContextIdentifier({
        labelID: params.labelID,
        categoryIDs: params.categoryIDs,
        conversationMode: params.conversationMode,
        filter: params.filter,
        sort: params.sort,
        from: params.search.from,
        to: params.search.to,
        address: params.search.address,
        begin: params.search.begin,
        end: params.search.end,
        keyword: params.search.keyword,
        newsletterSubscriptionID: params.newsletterSubscriptionID,
    });

    if (action.payload.isMove) {
        const elementIDs = action.payload.elements.map(({ ID }) => ID || '');
        state.bypassFilter = diff(state.bypassFilter, elementIDs);

        // Can update total only if move and is removing item from the current location (not all sent/all drafts/all mail)
        if (action.payload.elementTotalAdjustment && state.total) {
            state.total[contextFilter] = (state.total[contextFilter] || 0) + action.payload.elementTotalAdjustment;
        }
    }

    // If there is a filter applied when marking elements as read or unread, elements might need to bypass filters
    // e.g. filter is unread and marking elements as read, then we want to keep those items in the view
    if (action.payload.bypass && action.payload.markAsStatus) {
        const { conversationMode } = action.payload;
        const unreadFilter = state.params.filter.Unread as number | undefined;

        const { elementsToBypass, elementsToRemove } = getElementsToBypassFilter(
            action.payload.elements,
            action.payload.markAsStatus,
            unreadFilter
        );

        elementsToBypass.forEach((element) => {
            const isMessage = isElementMessage(element);
            const id = (isMessage && conversationMode ? element.ConversationID : element.ID) || '';
            if (!state.bypassFilter.includes(id)) {
                state.bypassFilter.push(id);
            }
        });

        // When some element bypass the filter in the current context, we need to update total in the "opposite" context
        const oppositeFilter = getElementContextIdentifier({
            labelID: params.labelID,
            categoryIDs: params.categoryIDs,
            conversationMode: params.conversationMode,
            filter: { Unread: unreadFilter ? 0 : 1 },
            sort: params.sort,
            from: params.search.from,
            to: params.search.to,
            address: params.search.address,
            begin: params.search.begin,
            end: params.search.end,
            keyword: params.search.keyword,
        });

        if (state.total[oppositeFilter]) {
            state.total[oppositeFilter] = state.total[oppositeFilter] + elementsToBypass.length;
        }

        // If we are not in a case where we need to bypass filter,
        // we need to remove elements if they are already in the array
        const toRemoveIDs = elementsToRemove.map((element) => {
            const isMessage = isElementMessage(element);
            return (isMessage && conversationMode ? element.ConversationID : element.ID) || '';
        });

        state.bypassFilter = state.bypassFilter.filter((elementID) => {
            return !toRemoveIDs.includes(elementID);
        });
    }
};

export const optimisticDelete = (state: Draft<ElementsState>, action: PayloadAction<OptimisticDelete>) => {
    action.payload.elementIDs.forEach((elementID) => {
        delete state.elements[elementID];
    });

    state.deletedSinceLastLoad += action.payload.elementIDs.length;

    if (state.total) {
        const params = state.params;

        const contextFilter = getElementContextIdentifier({
            labelID: params.labelID,
            categoryIDs: params.categoryIDs,
            conversationMode: params.conversationMode,
            filter: params.filter,
            sort: params.sort,
            from: params.search.from,
            to: params.search.to,
            address: params.search.address,
            begin: params.search.begin,
            end: params.search.end,
            keyword: params.search.keyword,
            newsletterSubscriptionID: params.newsletterSubscriptionID,
        });

        state.total[contextFilter] = (state.total[contextFilter] || 0) - action.payload.elementIDs.length;
    }
};

export const optimisticEmptyLabel = (state: Draft<ElementsState>) => {
    state.elements = {};
    state.page = 0;
};

export const backendActionStarted = (state: Draft<ElementsState>) => {
    state.pendingActions++;
};

export const backendActionFinished = (state: Draft<ElementsState>) => {
    state.pendingActions--;
};

export const selectAllFulfilled = (
    state: Draft<ElementsState>,
    { payload: { LabelID, timeoutID } }: PayloadAction<{ LabelID?: string; timeoutID?: NodeJS.Timeout }>
) => {
    if (LabelID && !state.taskRunning.labelIDs.includes(LabelID)) {
        state.taskRunning.labelIDs.push(LabelID);
    }
    if (timeoutID) {
        state.taskRunning.timeoutID = timeoutID;
    }
};

export const pollTaskRunningFulfilled = (state: Draft<ElementsState>, { payload }: PayloadAction<TaskRunningInfo>) => {
    state.taskRunning = payload;
};

export const deleteDraft = (state: Draft<ElementsState>, { payload: ID }: PayloadAction<string>) => {
    delete state.elements[ID];
};

const previousExpiration: Record<string, number | undefined> = {};

export const expireElementsPending = (
    state: Draft<ElementsState>,
    action: PayloadAction<
        void,
        string,
        { arg: { IDs: string[]; expirationTime: number | null; conversationID?: string } }
    >
) => {
    const { IDs, expirationTime, conversationID } = action.meta.arg;
    const copyIDs = [...IDs]; // Copy the array to avoid mutating the original one

    // Look to update the Conversation.ExpirationTime that contains the only message
    if (conversationID) {
        const conversation = state.elements[conversationID] as Conversation;

        if (conversation && conversation.NumMessages === 1) {
            copyIDs.push(conversationID);
        }
    }

    copyIDs.forEach((ID) => {
        const element = state.elements[ID];

        if (element) {
            previousExpiration[ID] = element.ExpirationTime;
            element.ExpirationTime = expirationTime ? expirationTime : undefined;
        }
    });
};

export const expireElementsFulfilled = (
    state: Draft<ElementsState>,
    action: PayloadAction<Promise<void>, string, { arg: { IDs: string[]; conversationID?: string } }>
) => {
    const { IDs, conversationID } = action.meta.arg;
    const copyIDs = [...IDs]; // Copy the array to avoid mutating the original one

    // Look to update the Conversation.ExpirationTime that contains the only message
    if (conversationID) {
        const conversation = state.elements[conversationID] as Conversation;

        if (conversation && conversation.NumMessages === 1) {
            copyIDs.push(conversationID);
        }
    }

    copyIDs.forEach((ID) => {
        delete previousExpiration[ID];
    });
};

export const expireElementsRejected = (
    state: Draft<ElementsState>,
    action: PayloadAction<unknown, string, { arg: { IDs: string[]; conversationID?: string } }>
) => {
    const { IDs, conversationID } = action.meta.arg;
    const copyIDs = [...IDs]; // Copy the array to avoid mutating the original one

    // Look to update the Conversation.ExpirationTime that contains the only message
    if (conversationID) {
        const conversation = state.elements[conversationID] as Conversation;

        if (conversation && conversation.NumMessages === 1) {
            copyIDs.push(conversationID);
        }
    }

    copyIDs.forEach((ID) => {
        const element = state.elements[ID];

        if (element) {
            element.ExpirationTime = previousExpiration[ID];
            delete previousExpiration[ID];
        }
    });
};

export const setParams = (
    state: Draft<ElementsState>,
    action: PayloadAction<Partial<ElementsStateParams> & { total?: number }>
) => {
    const { total, ...params } = action.payload;

    const prevContextFilter = getElementContextIdentifier({
        labelID: state.params.labelID,
        categoryIDs: state.params.categoryIDs,
        conversationMode: state.params.conversationMode,
        filter: state.params.filter,
        sort: state.params.sort,
        from: state.params.search.from,
        to: state.params.search.to,
        address: state.params.search.address,
        begin: state.params.search.begin,
        end: state.params.search.end,
        keyword: state.params.search.keyword,
    });

    const nextContextFilter = getElementContextIdentifier({
        labelID: params.labelID ?? state.params.labelID,
        categoryIDs: params.categoryIDs ?? state.params.categoryIDs,
        conversationMode: params.conversationMode ?? state.params.conversationMode,
        filter: params.filter,
        sort: params.sort,
        from: params.search?.from,
        to: params.search?.to,
        address: params.search?.address,
        begin: params.search?.begin,
        end: params.search?.end,
        keyword: params.search?.keyword,
    });

    const bypassFilterLength = state.bypassFilter.length;
    if (
        prevContextFilter !== nextContextFilter &&
        state.total[prevContextFilter] !== undefined &&
        bypassFilterLength > 0 &&
        state.total[prevContextFilter] >= bypassFilterLength
    ) {
        state.total[prevContextFilter] = state.total[prevContextFilter] - bypassFilterLength;
    }

    // Some items can bypass filter when a filter is active (e.g. Unread filter, and opening emails, we want them to stay in the list)
    // If sort or filter is being updated, we can reset bypass filter value from the state, otherwise it could create
    // false placeholders when switching filters.
    if (!isDeepEqual(state.params.sort, params.sort) || !isDeepEqual(state.params.filter, params.filter)) {
        state.bypassFilter = [];
    }

    state.params = {
        ...state.params,
        ...params,
    };

    // We reset the category IDs when the label is not inbox
    if (params.labelID && params.labelID !== MAILBOX_LABEL_IDS.INBOX) {
        state.params.categoryIDs = [];
    }

    if (total !== undefined) {
        state.total[nextContextFilter] = total;
        state.retry = newRetry(state.retry, state.params, undefined);
    }
};

const handleBypassFilter = (
    state: Draft<ElementsState>,
    elements: Element[],
    markAsStatus: MARK_AS_STATUS,
    unreadFilter: number | undefined
) => {
    if (unreadFilter === undefined) {
        return;
    }

    const shouldBypass =
        (unreadFilter === 1 && markAsStatus === MARK_AS_STATUS.READ) ||
        (unreadFilter === 0 && markAsStatus === MARK_AS_STATUS.UNREAD);

    if (shouldBypass) {
        const { elementsToBypass, elementsToRemove } = getElementsToBypassFilter(elements, markAsStatus, unreadFilter);

        // Add elements to bypass filter if they are not already present
        const elementsToBypassIDs = elementsToBypass.map((element) => element.ID || '');
        const newBypassIDs = elementsToBypassIDs.filter((id) => !state.bypassFilter.includes(id));
        state.bypassFilter.push(...newBypassIDs);

        // If we are not in a case where we need to bypass filter,
        // we need to remove elements if they are already in the array
        const toRemoveIDs = elementsToRemove.map((element) => {
            const isMessage = isElementMessage(element);
            return (isMessage && state.params.conversationMode ? element.ConversationID : element.ID) || '';
        });

        state.bypassFilter = state.bypassFilter.filter((elementID) => {
            return !toRemoveIDs.includes(elementID);
        });

        // When some element bypass the filter in the current context, we need to update total in the "opposite" context
        const oppositeFilter = getElementContextIdentifier({
            labelID: state.params.labelID,
            categoryIDs: state.params.categoryIDs,
            conversationMode: state.params.conversationMode,
            filter: { Unread: unreadFilter ? 0 : 1 },
            sort: state.params.sort,
            from: state.params.search.from,
            to: state.params.search.to,
            address: state.params.search.address,
            begin: state.params.search.begin,
            end: state.params.search.end,
            keyword: state.params.search.keyword,
        });

        if (state.total[oppositeFilter]) {
            state.total[oppositeFilter] = state.total[oppositeFilter] + elementsToBypass.length;
        }
    }
};

export const markMessagesAsReadPending = (
    state: Draft<ElementsState>,
    action: PayloadAction<
        undefined,
        string,
        {
            arg: {
                messages: MessageMetadata[];
                conversations: Conversation[];
                labelID: string;
                showSuccessNotification?: boolean;
            };
        }
    >
) => {
    const { messages, conversations } = action.meta.arg;

    messages.forEach((message) => {
        // Already marked as read, do nothing
        if (message.Unread === 0) {
            return;
        }

        const elementState = state.elements[message.ID];

        // Update the message selected in element state
        if (elementState && isElementMessage(elementState)) {
            elementState.Unread = 0;
        }

        const conversationElementState = state.elements[message.ConversationID];
        if (!conversationElementState || !isElementConversation(conversationElementState)) {
            return;
        }

        // Update the conversation element state attach to the same message
        conversationElementState.ContextNumUnread = safeDecreaseCount(conversationElementState.ContextNumUnread, 1);
        conversationElementState.NumUnread = safeDecreaseCount(conversationElementState.NumUnread, 1);
        conversationElementState.Labels?.forEach((label) => {
            if (message.LabelIDs.includes(label.ID)) {
                label.ContextNumUnread = safeDecreaseCount(label.ContextNumUnread, 1);
            }
        });
    });

    // When we open a conversation, an action is triggered to mark a message as read
    // We need to bypass filter for the conversation and the messages attached to it
    handleBypassFilter(state, [...messages, ...conversations], MARK_AS_STATUS.READ, state.params.filter.Unread);
};

export const markMessagesAsUnreadPending = (
    state: Draft<ElementsState>,
    action: PayloadAction<
        undefined,
        string,
        {
            arg: {
                messages: MessageMetadata[];
                conversations: Conversation[];
                labelID: string;
                showSuccessNotification?: boolean;
            };
        }
    >
) => {
    const { messages, conversations } = action.meta.arg;

    messages.forEach((message) => {
        // Already unread, do nothing
        if (message.Unread === 1) {
            return;
        }

        // Update elements state
        const elementState = state.elements[message.ID];

        if (elementState) {
            (elementState as Message).Unread = 1;
        }

        const conversationElementState = state.elements[message.ConversationID] as Conversation;
        if (!conversationElementState || !isElementConversation(conversationElementState)) {
            return;
        }

        conversationElementState.ContextNumUnread = safeIncreaseCount(conversationElementState.ContextNumUnread, 1);
        conversationElementState.NumUnread = safeIncreaseCount(conversationElementState.NumUnread, 1);
        conversationElementState.Labels?.forEach((label) => {
            if (message.LabelIDs.includes(label.ID)) {
                label.ContextNumUnread = safeIncreaseCount(label.ContextNumUnread, 1);
            }
        });
    });

    // When we open a conversation, an action is triggered to mark a message as unread
    // We need to bypass filter for the conversation and the messages attached to it
    handleBypassFilter(state, [...messages, ...conversations], MARK_AS_STATUS.UNREAD, state.params.filter.Unread);
};

export const markMessagesAsReadRejected = (
    state: Draft<ElementsState>,
    action: PayloadAction<
        unknown,
        string,
        {
            arg: {
                messages: MessageMetadata[];
                conversations: Conversation[];
                labelID: string;
                showSuccessNotification?: boolean;
            };
        }
    >
) => {
    const pendingAction = {
        ...action,
        payload: undefined,
    };
    return markMessagesAsUnreadPending(state, pendingAction);
};

export const markMessagesAsUnreadRejected = (
    state: Draft<ElementsState>,
    action: PayloadAction<
        unknown,
        string,
        {
            arg: {
                messages: MessageMetadata[];
                conversations: Conversation[];
                labelID: string;
                showSuccessNotification?: boolean;
            };
        }
    >
) => {
    const pendingAction = {
        ...action,
        payload: undefined,
    };
    return markMessagesAsReadPending(state, pendingAction);
};

export const markConversationsAsReadPending = (
    state: Draft<ElementsState>,
    action: PayloadAction<undefined, string, { arg: { conversations: Conversation[]; labelID: string } }>
) => {
    const { conversations, labelID } = action.meta.arg;

    conversations.forEach((conversation) => {
        const conversationLabel = conversation?.Labels?.find((label) => label.ID === labelID);
        if (conversationLabel?.ContextNumUnread === 0) {
            return;
        }
        const elementState = state.elements[conversation.ID];

        // Update the conversation element state
        if (elementState && isElementConversation(elementState)) {
            elementState.ContextNumUnread = 0;
            elementState.NumUnread = 0;
            elementState.Labels?.forEach((label) => {
                label.ContextNumUnread = 0;
            });
        }

        const messagesElementState = Object.values(state.elements)
            .filter(isElementMessage)
            .filter((message) => message.ConversationID === conversation.ID);

        // Update all messages attach to the same conversation in element state
        messagesElementState.forEach((messageElementState) => {
            messageElementState.Unread = 0;
        });
    });

    handleBypassFilter(state, conversations, MARK_AS_STATUS.READ, state.params.filter.Unread);
};

export const markConversationsAsUnreadPending = (
    state: Draft<ElementsState>,
    action: PayloadAction<undefined, string, { arg: { conversations: Conversation[]; labelID: string } }>
) => {
    const { conversations, labelID } = action.meta.arg;

    conversations.forEach((conversation) => {
        const conversationLabel = conversation?.Labels?.find((label) => label.ID === labelID);

        if (!!conversationLabel?.ContextNumUnread) {
            // Conversation is already unread, do nothing
            return;
        }

        // Update the conversation element state
        const elementState = state.elements[conversation.ID];
        if (!elementState || !isElementConversation(elementState)) {
            return;
        }

        const isConversationInInbox = hasLabel(elementState, MAILBOX_LABEL_IDS.INBOX);
        elementState.ContextNumUnread = safeIncreaseCount(elementState.ContextNumUnread, 1);
        elementState.NumUnread = safeIncreaseCount(elementState.NumUnread, 1);
        elementState.Labels?.forEach((label) => {
            if (label.ID === labelID) {
                label.ContextNumUnread = safeIncreaseCount(label.ContextNumUnread, 1);
            }

            // We can update the category label if the conversation is in the inbox
            if (labelID === MAILBOX_LABEL_IDS.INBOX && isConversationInInbox && isCategoryLabel(label.ID)) {
                label.ContextNumUnread = safeIncreaseCount(label.ContextNumUnread, 1);
            }
        });
    });

    handleBypassFilter(state, conversations, MARK_AS_STATUS.UNREAD, state.params.filter.Unread);
};

export const markConversationsAsReadRejected = (
    state: Draft<ElementsState>,
    action: PayloadAction<unknown, string, { arg: { conversations: Conversation[]; labelID: string } }>
) => {
    const pendingAction = {
        ...action,
        payload: undefined,
    };
    return markConversationsAsUnreadPending(state, pendingAction);
};

export const markConversationsAsUnreadRejected = (
    state: Draft<ElementsState>,
    action: PayloadAction<unknown, string, { arg: { conversations: Conversation[]; labelID: string } }>
) => {
    const pendingAction = {
        ...action,
        payload: undefined,
    };
    return markConversationsAsReadPending(state, pendingAction);
};

export const resetRetry = (state: Draft<ElementsState>) => {
    state.retry = newRetry(state.retry, state.params, undefined);
};

export const markNewsletterElementsAsReadPending = (
    state: Draft<ElementsState>,
    action: ReturnType<typeof filterSubscriptionList.pending>
) => {
    const payload = action.meta.arg;

    // The only action that have a visual impact is the mark as read
    if (!payload.data.MarkAsRead) {
        return;
    }

    Object.values(state.elements).forEach((element) => {
        if (isElementMessage(element) && element.NewsletterSubscriptionID === payload.subscription.ID) {
            element.Unread = 0;
        }
    });
};

export const labelMessagesPending = (
    state: Draft<ElementsState>,
    action: PayloadAction<
        undefined,
        string,
        {
            arg: {
                messages: MessageMetadata[];
                sourceLabelID: string;
                destinationLabelID: string;
                labels: Label[];
                folders: Folder[];
            };
        }
    >
) => {
    const { messages, sourceLabelID, destinationLabelID, labels, folders } = action.meta.arg;
    const countsBeforeAction = computeContextTotals(state);

    // We do not label message when changing categories, instead we perform a conversation label
    if (isCategoryLabel(destinationLabelID)) {
        traceInitiativeError(SentryMailInitiatives.MOVE_ACTIONS, 'elementsReducers: updating category label counts');
    } else {
        messages.forEach((message) => {
            // Update conversation first because we need to have the initial state of the element
            const conversationElementState = state.elements[message.ConversationID];
            if (conversationElementState && isElementConversation(conversationElementState)) {
                applyLabelToConversationMessage(
                    message,
                    conversationElementState,
                    sourceLabelID,
                    destinationLabelID,
                    labels
                );
            }

            const elementState = state.elements[message.ID];
            if (!elementState || !isElementMessage(elementState)) {
                return;
            }

            // Then update message since the mutation are done in the conversation
            applyLabelToMessage(elementState, destinationLabelID, folders, labels);
        });
    }

    const countsAfterAction = computeContextTotals(state);
    updateContextTotals({ state, countsBeforeAction, countsAfterAction });
    updateDeletedSinceLastLoadFromContextTotals({ state, countsBeforeAction, countsAfterAction });
};

export const unlabelMessagesPending = (
    state: Draft<ElementsState>,
    action: PayloadAction<
        undefined,
        string,
        {
            arg: {
                messages: MessageMetadata[];
                sourceLabelID: string;
                destinationLabelID: string;
                labels: Label[];
                folders: Folder[];
            };
        }
    >
) => {
    const { messages, destinationLabelID, labels } = action.meta.arg;
    const countsBeforeAction = computeContextTotals(state);

    messages.forEach((message) => {
        const conversationElementState = state.elements[message.ConversationID];

        if (conversationElementState && isElementConversation(conversationElementState)) {
            removeLabelToConversationMessage(message, conversationElementState, destinationLabelID, labels);
        }

        const elementState = state.elements[message.ID];
        if (!elementState || !isElementMessage(elementState)) {
            return;
        }

        removeLabelFromMessage(elementState, destinationLabelID, labels);
    });

    const countsAfterAction = computeContextTotals(state);
    updateContextTotals({ state, countsBeforeAction, countsAfterAction });
};

export const labelMessagesRejected = (
    state: Draft<ElementsState>,
    action: PayloadAction<
        unknown,
        string,
        { arg: { messages: MessageMetadata[]; destinationLabelID: string; labels: Label[]; folders: Folder[] } }
    >
) => {
    const { messages } = action.meta.arg;

    messages.forEach((message) => {
        const elementState = state.elements[message.ID];
        if (!elementState || !isElementMessage(elementState)) {
            return;
        }

        elementState.LabelIDs = message.LabelIDs;
        elementState.Unread = message.Unread;
    });
};

export const labelConversationsPending = (
    state: Draft<ElementsState>,
    action: PayloadAction<
        undefined,
        string,
        {
            arg: {
                conversations: Conversation[];
                destinationLabelID: string;
                sourceLabelID: string;
                labels: Label[];
                folders: Folder[];
            };
        }
    >
) => {
    const { conversations, sourceLabelID, destinationLabelID, labels, folders } = action.meta.arg;
    const countsBeforeAction = computeContextTotals(state);

    conversations.forEach((conversation) => {
        const conversationState = state.elements[conversation.ID];
        if (!conversationState) {
            return;
        }

        if (isElementConversation(conversationState)) {
            applyLabelToConversation(conversationState, sourceLabelID, destinationLabelID, labels, folders);
        }

        const messagesElementState = Object.values(state.elements)
            .filter(isElementMessage)
            .filter((message) => message.ConversationID === conversation.ID);

        messagesElementState.forEach((messageElementState) => {
            applyLabelToMessage(messageElementState, destinationLabelID, folders, labels);
        });
    });

    const countsAfterAction = computeContextTotals(state);
    updateContextTotals({ state, countsBeforeAction, countsAfterAction });
    updateDeletedSinceLastLoadFromContextTotals({ state, countsBeforeAction, countsAfterAction });
};

export const unlabelConversationsPending = (
    state: Draft<ElementsState>,
    action: PayloadAction<
        undefined,
        string,
        {
            arg: {
                conversations: Conversation[];
                sourceLabelID: string;
                destinationLabelID: string;
                labels: Label[];
                folders: Folder[];
            };
        }
    >
) => {
    const { conversations, destinationLabelID, labels } = action.meta.arg;
    const countsBeforeAction = computeContextTotals(state);

    conversations.forEach((conversation) => {
        const conversationState = state.elements[conversation.ID];
        if (!conversationState) {
            return;
        }

        if (isElementConversation(conversationState)) {
            removeLabelFromConversation(conversationState, destinationLabelID, labels);
        }

        const messagesElementState = Object.values(state.elements)
            .filter(isElementMessage)
            .filter((message) => message.ConversationID === conversation.ID);

        messagesElementState.forEach((messageElementState) => {
            removeLabelFromMessage(messageElementState, destinationLabelID, labels);
        });
    });

    const countsAfterAction = computeContextTotals(state);
    updateContextTotals({ state, countsBeforeAction, countsAfterAction });
};

export const loadConversationFulfilled = (
    state: Draft<ElementsState>,
    action: PayloadAction<ConversationResult, string, { arg: ConversationParams }>
) => {
    const { Conversation } = action.payload;
    state.elements[Conversation.ID] = Conversation;
};
